import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Slider,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { AutoRefreshConfig } from '../hooks/useAutoRefreshConfig';

interface AutoRefreshSettingsModalProps {
  open: boolean;
  onClose: () => void;
  config: AutoRefreshConfig;
  onSave: (config: AutoRefreshConfig) => void;
}

const INTERVAL_OPTIONS = [
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 15, label: '15s' },
  { value: 20, label: '20s' },
  { value: 25, label: '25s' },
  { value: 30, label: '30s' },
];

export const AutoRefreshSettingsModal: React.FC<AutoRefreshSettingsModalProps> = ({
  open,
  onClose,
  config,
  onSave,
}) => {
  const [enabled, setEnabled] = useState(config.enabled);
  const [intervalSeconds, setIntervalSeconds] = useState(config.intervalSeconds);

  const handleSave = () => {
    onSave({
      enabled,
      intervalSeconds,
    });
    onClose();
  };

  const handleCancel = () => {
    // Reset to current config values
    setEnabled(config.enabled);
    setIntervalSeconds(config.intervalSeconds);
    onClose();
  };

  const getIntervalLabel = (seconds: number) => {
    const option = INTERVAL_OPTIONS.find(opt => opt.value === seconds);
    return option ? option.label : `${seconds}s`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Auto-Refresh Settings</DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Enable auto-refresh for expanded projects"
          />
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 3 }}>
            Automatically refreshes test case status for currently expanded projects
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Refresh Interval: {getIntervalLabel(intervalSeconds)}
          </Typography>
          
          <Slider
            value={intervalSeconds}
            onChange={(_, value) => setIntervalSeconds(value as number)}
            min={5}
            max={30}
            step={null}
            marks={INTERVAL_OPTIONS.map(opt => ({
              value: opt.value,
              label: opt.label,
            }))}
            disabled={!enabled}
            valueLabelDisplay="off"
            sx={{ mt: 2, mb: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};